/*
 * The phone field (design Req 10): formats as you type, US +1 by default with
 * a country switcher, and hands back E.164 for the server. Formatting and
 * validation come from libphonenumber-js (min metadata); why this library and
 * not @maskito/phone is the `phone-format-dependency` row of
 * .valinor/research/README.md.
 */
import React, { useMemo } from "react";
import { AsYouType, getCountries, getCountryCallingCode, parsePhoneNumberFromString } from "libphonenumber-js/min";

export const DEFAULT_COUNTRY = "US";

// The number as typed, formatted for its country ("(415) 555-0134").
export const formatPhone = (raw, country) => new AsYouType(country).input(raw || "");

// E.164 ("+14155550134") when the number is a possible, valid one; else null.
export const toE164 = (raw, country) => {
  const parsed = parsePhoneNumberFromString(raw || "", country);
  return parsed && parsed.isValid() ? parsed.number : null;
};

// An E.164 number for reading back: national for the default country
// ("(415) 555-0134"), international otherwise ("+44 20 7946 0958").
export const displayPhone = (e164) => {
  const parsed = parsePhoneNumberFromString(e164 || "");
  if (!parsed) return e164 || "";
  return parsed.country === DEFAULT_COUNTRY ? parsed.formatNational() : parsed.formatInternational();
};

const PhoneField = ({
  id,
  label,
  countryLabel,
  value,
  country,
  onChange,
  onCountryChange,
  invalid,
  disabled,
  describedBy,
  autoFocus,
}) => {
  const countries = useMemo(
    () => getCountries().map((c) => ({ c, code: getCountryCallingCode(c) })),
    []
  );
  const dial = getCountryCallingCode(country);

  return (
    <div className="fld">
      <label htmlFor={id}>{label}</label>
      <div className={`inp${invalid ? " err" : ""}${disabled ? " dis" : ""}`}>
        <span className="cc">
          <span aria-hidden="true">{`${country} +${dial}`}</span>
          <select
            aria-label={countryLabel}
            value={country}
            onChange={(e) => onCountryChange(e.target.value)}
            disabled={disabled}
          >
            {countries.map(({ c, code }) => (
              <option key={c} value={c}>{`${c} +${code}`}</option>
            ))}
          </select>
        </span>
        <input
          id={id}
          name="phone"
          type="tel"
          inputMode="tel"
          autoComplete="tel-national"
          value={value}
          // Deleting keeps the raw text: re-formatting "(415)" would put the
          // ")" straight back, and backspace could never get past it.
          onChange={(e) => {
            const raw = e.target.value;
            onChange(raw.length < value.length ? raw : formatPhone(raw, country));
          }}
          disabled={disabled}
          aria-invalid={invalid || undefined}
          aria-describedby={describedBy}
          // eslint-disable-next-line jsx-a11y/no-autofocus
          autoFocus={autoFocus}
        />
      </div>
    </div>
  );
};

export default PhoneField;
